/* eslint-disable max-lines, max-len */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import UndoIcon from '@mui/icons-material/Undo';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Chip, Collapse, Divider, IconButton, Paper, Typography } from '@mui/material';
import { keyframes, useTheme } from '@mui/material/styles';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const itemShape = PropTypes.shape({
  lineItemId: PropTypes.string.isRequired,
  name: PropTypes.string,
  sku: PropTypes.string,
  orderId: PropTypes.string,
  quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

const TargetShipmentAccordion = ({
  targetShipment,
  droppedHere,
  originalItems,
  isDropDisabled,
  pickedItems,
  fulfillingPharmacyName,
  hasDroppedItems,
  isPickLocked,
  expanded,
  onToggle,
  onUndoItem,
  onUndoAll,
  onMoveAll,
  pinnedRole,
  isManualShipment,
  isDraggingFromTarget,
  isDraggingFromLeft,
}) => {
  const theme = useTheme();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (droppedHere.length > 0 && scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [droppedHere.length]);

  const pickedFromHere = pickedItems
    .filter((i) => i.sourceShipmentId === targetShipment.shipmentId)
    .map((i) => i.lineItemId);

  const hasDropped = droppedHere.length > 0;
  const isSelected = hasDropped || pickedFromHere.length > 0 || expanded;

  const existingItems = (targetShipment.shipmentItems || []).filter(
    (i) => !pickedFromHere.includes(i.lineItemId)
  );

  const totalItems = existingItems.length + droppedHere.length;

  const totalQty =
    existingItems.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0) +
    droppedHere.reduce((sum, item) => {
      const orig = originalItems.find((i) => i.lineItemId === item.referenceId);
      return sum + (Number(orig?.quantity) || 0);
    }, 0);

  const address = targetShipment.shipmentAddress;

  return (
    <Paper
      variant='outlined'
      sx={{
        mb: 1.5,
        borderRadius: 2,
        overflow: 'hidden',
        borderColor: isSelected ? 'primary.main' : 'divider',
        borderWidth: isSelected ? 2 : 1,
        transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
      }}
    >
      {/* Card header — entire row is clickable */}
      <Box
        onClick={onToggle}
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Box sx={{ mt: '2px', color: isSelected ? 'primary.main' : 'text.disabled' }}>
          {isSelected ? (
            <RadioButtonCheckedIcon sx={{ fontSize: 20 }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.4 }}>
            <Typography variant='body2' fontWeight={700}>
              Order: {targetShipment.referenceId}
            </Typography>
            <Chip
              label={`${totalItems} ${totalItems === 1 ? 'item' : 'items'}`}
              size='small'
              color={hasDropped ? 'primary' : 'default'}
              sx={{ height: 20, fontSize: 11 }}
            />
            {hasDropped && (
              <Chip
                label={`+${droppedHere.length} incoming`}
                size='small'
                color='success'
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
            {pickedFromHere.length > 0 && (
              <Chip
                label={`${pickedFromHere.length} Picked`}
                size='small'
                variant='outlined'
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'rgba(126,87,194,0.45)',
                  borderColor: 'rgba(126,87,194,0.45)',
                }}
              />
            )}
            {isManualShipment && (
              <Chip
                label='Manual'
                size='small'
                variant='outlined'
                color='primary'
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </Box>
          {fulfillingPharmacyName && (
            <Typography variant='caption' color='text.secondary' display='block'>
              Facility: {fulfillingPharmacyName}
            </Typography>
          )}
          {(targetShipment.partnerName || targetShipment.practiceName) && (
            <Typography
              variant='caption'
              color='text.secondary'
              display='block'
              noWrap
              sx={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {targetShipment.partnerName && <>Partner: {targetShipment.partnerName}</>}
              {targetShipment.partnerName && targetShipment.practiceName && <>&nbsp;·&nbsp;</>}
              {targetShipment.practiceName && <>Practice: {targetShipment.practiceName}</>}
            </Typography>
          )}
          {targetShipment.recipientName && (
            <Typography variant='caption' color='text.secondary'>
              Ship to: {targetShipment.recipientName}
              {address?.city && (
                <>
                  {' '}
                  &nbsp;·&nbsp; {address.city}, {address.state} {address.zip}
                </>
              )}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: '-4px' }}>
          {pinnedRole && (
            <Chip
              label='📌 Pinned'
              size='small'
              variant='outlined'
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 600,
                color: 'text.secondary',
                borderColor: 'text.disabled',
              }}
            />
          )}
          <Box sx={{ color: 'text.secondary' }}>
            {expanded ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
          </Box>
        </Box>
      </Box>

      {/* Expanded section */}
      <Collapse in={expanded} timeout={isDraggingFromLeft ? 0 : 250}>
        <Divider />
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'grey.50',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='caption' fontWeight={600} color='text.secondary'>
              Items in this order
            </Typography>
            {onMoveAll && existingItems.length > 0 && (
              <Button
                size='small'
                variant='outlined'
                color='primary'
                startIcon={<ArrowBackIcon sx={{ fontSize: 12 }} />}
                onClick={onMoveAll}
                sx={{
                  fontSize: 10,
                  textTransform: 'none',
                  height: 22,
                  px: 1,
                  py: 0,
                  fontWeight: 600,
                  borderRadius: 2,
                  lineHeight: 1,
                  '& .MuiButton-startIcon': { marginRight: '4px' },
                }}
              >
                Move All ({existingItems.length})
              </Button>
            )}
          </Box>
          <Typography variant='caption' color='text.secondary'>
            Total Qty: {totalQty}
          </Typography>
        </Box>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Droppable droppableId={targetShipment.shipmentId} isDropDisabled={isDropDisabled}>
            {(provided, snapshot) => (
              <Box
                ref={(el) => {
                  provided.innerRef(el);
                  scrollRef.current = el;
                }}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...provided.droppableProps}
                sx={{
                  minHeight: 52,
                  maxHeight: 260,
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: `${theme.palette.divider} transparent`,
                  '&::-webkit-scrollbar': { width: 4 },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.divider,
                    borderRadius: 4,
                  },
                  borderRadius: 1,
                  border: snapshot.isDraggingOver
                    ? `2px dashed ${theme.palette.primary.main}`
                    : `2px dashed ${theme.palette.divider}`,
                  bgcolor: snapshot.isDraggingOver ? 'primary.50' : 'transparent',
                  transition: 'all 0.2s ease',
                  p: 1,
                }}
              >
                {/* Existing shipment items */}
                {map(existingItems, (item, index) => (
                  <Draggable
                    key={`pick-${item.lineItemId}`}
                    draggableId={`pick-${item.lineItemId}`}
                    index={index}
                    isDragDisabled={hasDroppedItems || isPickLocked}
                  >
                    {(drag, dSnap) => (
                      <Box
                        ref={drag.innerRef}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...drag.draggableProps}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...drag.dragHandleProps}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1,
                          py: 0.75,
                          opacity: dSnap.isDragging ? 0.7 : 1,
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor:
                              hasDroppedItems || isPickLocked ? 'transparent' : 'action.hover',
                          },
                          cursor: hasDroppedItems || isPickLocked ? 'default' : 'grab',
                        }}
                      >
                        <DragIndicatorIcon
                          sx={{
                            color:
                              hasDroppedItems || isPickLocked ? 'transparent' : 'text.disabled',
                            fontSize: 16,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant='body2'>{item.name || item.sku}</Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {item.orderId && <>Order: {item.orderId}&nbsp;·&nbsp;</>}Qty:{' '}
                            {item.quantity}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Draggable>
                ))}

                {/* Picked items — greyed out */}
                {pickedFromHere.map((lineItemId) => {
                  const item = (targetShipment.shipmentItems || []).find(
                    (i) => i.lineItemId === lineItemId
                  );
                  if (!item) return null;
                  return (
                    <Box
                      key={`picked-${lineItemId}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.75,
                        opacity: 0.45,
                        borderRadius: 1,
                      }}
                    >
                      <DragIndicatorIcon sx={{ color: 'transparent', fontSize: 16 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                          {item.name || item.sku}
                        </Typography>
                        <Typography variant='caption' color='text.disabled'>
                          {item.orderId && <>Order: {item.orderId}&nbsp;·&nbsp;</>}Qty:{' '}
                          {item.quantity}
                        </Typography>
                      </Box>
                      <Chip
                        label='Picked'
                        size='small'
                        variant='outlined'
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#7E57C2',
                          borderColor: '#7E57C2',
                        }}
                      />
                    </Box>
                  );
                })}

                {/* Drop zone hint */}
                {droppedHere.length === 0 && !isDraggingFromTarget && (
                  <Typography
                    variant='caption'
                    color='text.disabled'
                    sx={{ display: 'block', textAlign: 'center', py: 0.5 }}
                  >
                    Drop items here to move
                  </Typography>
                )}

                {/* Incoming section header */}
                {droppedHere.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1,
                      py: 0.75,
                      mt: 1,
                      bgcolor: 'success.50',
                      borderTop: '1px solid',
                      borderBottom: '1px solid',
                      borderColor: 'success.light',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant='caption'
                        color='success.dark'
                        fontWeight={700}
                        sx={{ letterSpacing: 0.3 }}
                      >
                        Incoming Items
                      </Typography>
                      <Chip
                        label={droppedHere.length}
                        size='small'
                        color='success'
                        sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                      />
                    </Box>
                    <Button
                      size='small'
                      onClick={onUndoAll}
                      sx={{
                        fontSize: 10,
                        textTransform: 'none',
                        color: 'text.secondary',
                        p: 0,
                        minWidth: 0,
                        lineHeight: 1,
                      }}
                    >
                      Reset all
                    </Button>
                  </Box>
                )}

                {droppedHere.length > 0 && <Box sx={{ mt: 0.75 }} />}
                {map(droppedHere, (item, index) => {
                  const orig = originalItems.find((i) => i.lineItemId === item.referenceId);
                  return orig ? (
                    <Draggable
                      key={item.referenceId}
                      draggableId={item.referenceId}
                      index={existingItems.length + index}
                      isDragDisabled
                    >
                      {(drag, dSnap) => (
                        <Box
                          ref={drag.innerRef}
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...drag.draggableProps}
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...drag.dragHandleProps}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1,
                            py: 0.75,
                            mb: 0.25,
                            borderRadius: 1,
                            animation: `${fadeSlideUp} 0.25s ease-out both`,
                            opacity: dSnap.isDragging ? 0.7 : 1,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant='body2' fontWeight={500}>
                              {orig.name || orig.sku}
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                              {orig.orderId && <>Order: {orig.orderId}&nbsp;·&nbsp;</>}Qty:{' '}
                              {orig.quantity}
                            </Typography>
                          </Box>
                          <Chip label='Incoming' size='small' color='success' />
                          <IconButton
                            size='small'
                            title='Undo — move back to original shipment'
                            onClick={() => onUndoItem(item.referenceId)}
                          >
                            <UndoIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      )}
                    </Draggable>
                  ) : null;
                })}

                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </Box>
      </Collapse>
    </Paper>
  );
};

TargetShipmentAccordion.propTypes = {
  targetShipment: PropTypes.shape({
    shipmentId: PropTypes.string.isRequired,
    referenceId: PropTypes.string.isRequired,
    recipientName: PropTypes.string,
    partnerName: PropTypes.string,
    practiceName: PropTypes.string,
    shipmentAddress: PropTypes.shape({
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
    shipmentItems: PropTypes.arrayOf(itemShape),
  }).isRequired,
  droppedHere: PropTypes.arrayOf(PropTypes.shape({ referenceId: PropTypes.string.isRequired }))
    .isRequired,
  originalItems: PropTypes.arrayOf(itemShape).isRequired,
  isDropDisabled: PropTypes.bool,
  pickedItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      sourceShipmentId: PropTypes.string.isRequired,
    })
  ),
  fulfillingPharmacyName: PropTypes.string,
  hasDroppedItems: PropTypes.bool,
  isPickLocked: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  onUndoItem: PropTypes.func,
  onUndoAll: PropTypes.func,
  onMoveAll: PropTypes.func,
  pinnedRole: PropTypes.string,
  isManualShipment: PropTypes.bool,
  isDraggingFromTarget: PropTypes.bool,
  isDraggingFromLeft: PropTypes.bool,
};

TargetShipmentAccordion.defaultProps = {
  isDropDisabled: false,
  pickedItems: [],
  fulfillingPharmacyName: '',
  hasDroppedItems: false,
  isPickLocked: false,
  expanded: false,
  onToggle: undefined,
  onUndoItem: undefined,
  onUndoAll: undefined,
  onMoveAll: undefined,
  pinnedRole: null,
  isManualShipment: false,
  isDraggingFromTarget: false,
  isDraggingFromLeft: false,
};

export default TargetShipmentAccordion;
